import { composeSection } from "./compose.js";
import { el } from "./svg-model.js";
import { timeFixtureHousing } from "./time-fixtures.js";
import { outlineSymbols } from "./recovery.js";
/** Only coordinates used by the renderer participate. Edits to narrative tells
 * never invalidate an art kit; a moved slot or floor always does. */
export function environmentLayout(definition) {
    return JSON.stringify({ shell: { bays: definition.shell.bays, bayWidthCm: definition.shell.bayWidthCm, floorToFloorCm: definition.shell.floorToFloorCm }, rooms: definition.rooms.map(({ id, floor, bays, slots }) => ({ id, floor, bays, slots })) });
}
/**
 * The Release A framing rule when nobody chose a camera: the painted room the
 * player stands in; otherwise the painted room holding the most people, so a
 * session's first frame is a coherent illustrated room rather than the whole
 * mixed-medium building. A room the kit could not apply to is never framed as
 * painted. Presentation only: the whole building stays one control away, and
 * the choice reads visible presence, never hidden importance (plan step 7).
 */
function defaultFocus(applied, view) {
    const playerRoom = view.actors.find((actor) => actor.instanceId === "player")?.room;
    const withPlayer = applied.find((room) => room.room === playerRoom);
    if (withPlayer !== undefined)
        return withPlayer;
    let best;
    let most = -1;
    for (const room of applied) {
        const present = view.actors.filter((actor) => actor.room === room.room).length;
        if (present > most) {
            best = room;
            most = present;
        }
    }
    return best;
}
/** The kit rule that draws this person here, or undefined: the room then keeps
 * its accurate composed drawing. Exported so the coverage audit asks the
 * renderer's own question instead of a copy of it. */
export function livingActorRule(roomKit, slotId, actor) {
    return roomKit.actors.find((rule) => (rule.instanceIds === undefined || rule.instanceIds.includes(actor.instanceId)) && rule.symbols.includes(actor.symbolRef) && rule.poses.includes(actor.pose) && rule.facings.includes(actor.facing) && rule.slots.includes(slotId));
}
/** Rendering is a view over immutable physical state. No event, room, actor or
 * object is added to the story. Unsupported states keep their accurate drawing. */
export function composeLivingSection(input) {
    const { definition: source, view: original, symbols, kit } = input;
    const gaps = [];
    if (kit === null || kit.envId !== source.envId || kit.layout !== environmentLayout(source)) {
        return { document: composeSection({ definition: source, view: original, symbols }), rooms: [], gaps: ["layout-or-version-unavailable"], version: null, lighting: "soft-wash" };
    }
    let definition = { ...source, rooms: [...source.rooms] };
    let view = { ...original, actors: [...original.actors], props: [...original.props], fixtures: [...original.fixtures] };
    const applied = [];
    // One coherent light per shot: only the room the player is in may turn, never
    // the whole-building view, and only with every backdrop piece present.
    const litRoom = original.lighting === "hard-light" && input.focus !== null ? original.actors.find((actor) => actor.instanceId === "player")?.room ?? null : null;
    let lighting = "soft-wash";
    for (const roomKit of kit.rooms) {
        const room = source.rooms.find((room) => room.id === roomKit.room);
        if (room === undefined)
            continue;
        const actors = original.actors.filter((actor) => actor.room === room.id);
        const rules = actors.map((actor) => livingActorRule(roomKit, room.slots[actor.slot]?.id ?? "", actor));
        const replacementIds = actors.map((actor, i) => actor.propRefs.length > 0 ? rules[i]?.holdingReplacement ?? rules[i]?.replacement : rules[i]?.replacement);
        const backdrop = roomKit.room === litRoom && roomKit.hardLight !== undefined && roomKit.hardLight.fixed.every((piece) => symbols.has(piece.symbol)) ? roomKit.hardLight : null;
        const fixed = backdrop === null ? [...roomKit.fixed] : [...backdrop.fixed, ...roomKit.fixed.filter((piece) => !backdrop.drops.includes(piece.symbol))];
        for (const seat of roomKit.seats ?? []) {
            const slot = room.slots.find((slot) => slot.id === seat.slot);
            if (slot === undefined)
                continue;
            const occupant = actors.find((actor) => room.slots[actor.slot]?.id === seat.slot && actor.pose === "sit");
            const facing = occupant?.facing ?? seat.facing;
            // Hip placement mirrors with the sitter; navigation coordinates do not.
            const dx = facing === "left" ? seat.offsetX : -seat.offsetX;
            let x = slot.x + dx, y = slot.y + seat.offsetY;
            const index = occupant === undefined ? -1 : actors.indexOf(occupant);
            const actorMeta = symbols.get(replacementIds[index] ?? "")?.meta;
            const chairMeta = symbols.get(seat.rear)?.meta;
            const hip = actorMeta?.illustration?.contacts.find((c) => c.kind === "seat");
            const contact = chairMeta?.illustration?.contacts.find((c) => c.kind === "seat");
            if (actorMeta !== undefined && chairMeta !== undefined && hip !== undefined && contact !== undefined) {
                const actorSign = actorMeta.illustration?.nativeFacing === facing ? 1 : -1;
                const chairSign = chairMeta.illustration?.nativeFacing === facing ? 1 : -1;
                x = slot.x + (rules[index]?.offsetX ?? 0) + actorSign * (hip.x - actorMeta.anchor.x) - chairSign * (contact.x - chairMeta.anchor.x);
                y = slot.y + (rules[index]?.offsetY ?? 0) + actorMeta.anchor.y - hip.y - (chairMeta.anchor.y - contact.y);
            }
            fixed.push({ symbol: seat.rear, x, y, facing });
            if (occupant !== undefined)
                fixed.push({ symbol: seat.front, x, y, facing });
        }
        if (rules.some((rule) => rule === undefined) || [...fixed.map((item) => item.symbol), ...replacementIds].some((id) => id === undefined || !symbols.has(id))) {
            gaps.push(`${room.id}:unsupported-pose-place-facing-or-asset`);
            continue;
        }
        // Legacy pilot kits do not expose object contacts; modular kits preserve live props.
        if (roomKit.modular !== true && (actors.some((actor) => actor.propRefs.length > 0) || original.props.some((prop) => prop.room === room.id && !roomKit.replaceProps.includes(prop.symbolRef)))) {
            gaps.push(`${room.id}:dynamic-object`);
            continue;
        }
        const slots = [...room.slots, ...fixed.map((item, i) => ({ id: `art-fixed-${String(i)}`, x: item.x, y: item.y, kind: "surface" }))];
        const replacements = actors.map((actor, i) => {
            const rule = rules[i];
            const slot = room.slots[actor.slot];
            if (rule === undefined || slot === undefined)
                return actor;
            const renderSlot = slots.length;
            slots.push({ ...slot, id: `art-actor-${String(i)}`, x: slot.x + rule.offsetX, y: slot.y + rule.offsetY });
            return { ...actor, slot: renderSlot, symbolRef: replacementIds[i] ?? rule.replacement };
        });
        const mappedProps = original.props.filter((prop) => prop.room === room.id && !roomKit.replaceProps.includes(prop.symbolRef)).map((prop, i) => {
            const slot = room.slots[prop.at];
            const rule = roomKit.props?.find((rule) => rule.symbols.includes(prop.symbolRef) && (rule.slots === undefined || rule.slots.includes(slot?.id ?? "")));
            if (rule === undefined || slot === undefined || !symbols.has(rule.replacement))
                return prop;
            const at = slots.length;
            slots.push({ ...slot, id: `art-prop-${String(i)}`, x: slot.x + rule.offsetX, y: slot.y + rule.offsetY });
            return { ...prop, at, symbolRef: rule.replacement };
        });
        definition = { ...definition, rooms: definition.rooms.map((item) => item.id === room.id ? { ...item, slots } : item), props: [...definition.props.filter((prop) => prop.room !== room.id || !roomKit.replaceProps.includes(prop.symbolRef)), ...fixed.map((item, i) => ({ symbolRef: item.symbol, room: room.id, slot: `art-fixed-${String(i)}`, variant: null, facing: item.facing ?? "right" }))] };
        view = { ...view, actors: view.actors.map((actor) => replacements.find((replacement) => replacement.instanceId === actor.instanceId) ?? actor),
            props: [...view.props.filter((prop) => prop.room !== room.id), ...fixed.map((item, i) => ({ symbolRef: item.symbol, room: room.id, at: room.slots.length + i, variant: null })), ...mappedProps],
            fixtures: view.fixtures.filter((fixture) => !roomKit.replaceFixtures.includes(fixture.id)),
        };
        applied.push(roomKit);
        if (backdrop !== null)
            lighting = "hard-light";
    }
    const whole = input.focus === null;
    const focused = whole ? undefined : input.focus === undefined ? defaultFocus(applied, view) : applied.find((room) => room.room === input.focus);
    if (whole || focused?.modular === true)
        definition = { ...definition, shell: { ...definition.shell, focus: null } };
    // A room camera must not download every off-camera painting in the building.
    // Off-camera instances retain their identity/geometry as network-free drawings;
    // selecting the whole building restores their full registered artwork.
    let cameraSymbols = symbols;
    if (focused !== undefined) {
        const visibleRefs = new Set([
            ...view.actors.filter((actor) => actor.room === focused.room).map((actor) => actor.symbolRef),
            ...view.actors.filter((actor) => actor.room === focused.room).flatMap((actor) => actor.propRefs),
            ...view.props.filter((prop) => prop.room === focused.room).map((prop) => prop.symbolRef),
            // Someone on the phone with a person in this room is drawn here too, as an inset with a handset.
            ...(view.call !== undefined && view.actors.some((actor) => actor.instanceId === view.call?.with && actor.room === focused.room) ? [view.call.symbolRef, "ink.shared.a14-office-devices.phone"] : []),
            ...view.fixtures.filter((fixture) => source.fixtures.find((item) => item.id === fixture.id)?.room === focused.room).flatMap((fixture) => [fixture.symbolRef, timeFixtureHousing(fixture.symbolRef)].filter((id) => id !== null)),
        ]);
        for (const id of symbols.keys())
            if ([...visibleRefs].some((ref) => id.startsWith(`${ref}-`)))
                visibleRefs.add(id);
        const queue = [...visibleRefs];
        while (queue.length > 0) {
            const id = queue.pop();
            if (id === undefined)
                break;
            for (const match of symbols.get(id)?.markup.matchAll(/<use\b[^>]*href="#([^"]+)"/gu) ?? []) {
                const ref = match[1];
                if (ref !== undefined && !visibleRefs.has(ref)) {
                    visibleRefs.add(ref);
                    queue.push(ref);
                }
            }
        }
        cameraSymbols = new Map([...outlineSymbols(symbols)].map(([id, fallback]) => [id, visibleRefs.has(id) ? symbols.get(id) ?? fallback : fallback]));
    }
    let document = composeSection({ definition, view, symbols: cameraSymbols, ...(focused?.modular === true ? { cameraRoom: focused.room } : {}), contactRooms: new Set(applied.filter((room) => room.modular === true).map((room) => room.room)) });
    // Each painted room owns its lighting. Remove only its extra light/labels;
    // unchanged rooms retain their existing treatment and all their occupants.
    const active = new Set(applied.map((room) => room.room));
    const prune = (nodes, roomId) => nodes.filter((node) => !(active.has(roomId ?? "") && node.attrs["data-layer"] === "window-light") && !(focused !== undefined && node.attrs["data-layer"] === "labels") && !(focused?.modular === true && ["shell", "sky", "window-light"].includes(node.attrs["data-layer"] ?? ""))).map((node) => ({ ...node, attrs: focused?.modular === true && node.attrs["data-room"] !== undefined && node.attrs["data-room"] !== focused.room ? { ...node.attrs, display: "none" } : node.attrs, children: prune(node.children, node.attrs["data-room"] ?? roomId) }));
    document = { ...document, body: prune(document.body) };
    if (focused !== undefined) {
        const player = original.actors.find((actor) => actor.instanceId === "player" && actor.room === focused.room) ?? original.actors.find((actor) => actor.room === focused.room);
        const station = source.rooms.find((room) => room.id === focused.room)?.slots[player?.slot ?? -1]?.id;
        const cropId = input.crop ?? (focused.frame.w > 850 && station !== undefined ? `at-${station}` : "overview");
        const frame = focused.crops?.find((crop) => crop.id === cropId)?.frame ?? focused.frame;
        const { x, y, w, h } = frame;
        const id = `living-frame-${kit.id}-${focused.room}`;
        document = { ...document, viewBox: frame, reveal: null,
            defs: [...document.defs, el("clipPath", { id }, [el("rect", { x: String(x), y: String(y), width: String(w), height: String(h) })])],
            body: [el("g", { "clip-path": `url(#${id})` }, document.body)],
        };
    }
    return { document, rooms: applied.map((room) => room.room), gaps, version: applied.length > 0 ? kit.id : null, lighting };
}
//# sourceMappingURL=living.js.map